import { FormattedDate } from 'react-intl';

import { useGetAccount } from 'apps-common/hooks/useGetAccount';
import { PlanStatus, UserPermission } from 'apps-common/types';
import { track } from 'apps-common/utils/analytics';
import { Experiments, useExperiment } from 'apps-common/utils/experiments';
import { Flags, useFlag } from 'apps-common/utils/featureFlags';
import { isUserInactivePrepaidPending, membershipOfferingPrepaidMonths } from 'apps-common/utils/member';
import { t } from 'translations';
import { SpotlightItem } from 'ui';

import { routes } from '../routes';

export const SpotlightItemController = ({ children }: { children?: React.ReactNode }) => {
  // Experiments
  const inactivePrepaidPendingSpotlightItemExperiment = !!useExperiment(
    Experiments.INACTIVE_PREPAID_PENDING_SPOTLIGHT_ITEM,
  ).experiment.get('showSpotlightItem');
  const cancelledSpotlightItemExperiment = useExperiment(Experiments.CANCELLED_SPOTLIGHT_ITEM).experiment.get(
    'showPendingCancellationSpotlightItem',
    false,
  );
  // Feature flags
  const billingTransitionFlag = useFlag(Flags.BILLING_TRANSITION);

  const { data: accountData, isLoading } = useGetAccount();

  if (isLoading || !accountData) return <>{children}</>;

  const { account, membershipOffering } = accountData ?? {};
  const { currentSubscription, b2bPartnerName, membership, accessProfile } = account ?? {};
  const { pendingCancellation, plans } = membership ?? {};
  const { permissions = [] } = accessProfile ?? {};

  /**
   * Render logic
   *
   * - Render only one spotlight item at a time
   * - SpotlightItemController takes children prop to control hiding/showing them based on existence of a spotlight item
   */

  // SPOTLIGHT ITEM: User eligible to convert from B2B to D2C
  if (billingTransitionFlag && permissions.includes(UserPermission.canConvertToD2C)) {
    const endDate = plans?.find((plan) => plan.status === PlanStatus.PRESENT)?.endDate;
    if (!endDate) return <>{children}</>;

    const partnerName = b2bPartnerName ?? t('membership_spotlight_b2b_to_d2c_partner_fallback');

    return (
      <SpotlightItem
        data-testid="spotlight-b2btod2c"
        label={t('membership_spotlight_b2b_to_d2c_label', {
          partnerName,
          endDate: <FormattedDate value={endDate} format="default" />,
        })}
        ctaButton={{
          to: routes.addressForm,
          children: t('membership_spotlight_b2b_to_d2c_cta'),
          onClick: () => {
            track({
              event: 'Link Clicked',
              payload: {
                cta: 'billing_transition',
                location: 'card',
              },
            });
          },
        }}
        textContents={[t('membership_spotlight_b2b_to_d2c_text')]}
      />
    );
  }

  // SPOTLIGHT ITEM: Inactive Prepaid Pending
  if (
    inactivePrepaidPendingSpotlightItemExperiment &&
    isUserInactivePrepaidPending(currentSubscription, membershipOffering)
  ) {
    return (
      <>
        <SpotlightItem
          data-testid="spotlight-ipp"
          label={t('inactive_prepaid_pending_membership_renew_pill_title')}
          ctaButton={{
            to: routes.reviewPaymentMethodForRenew,
            children: t('membership_hub_renew_submit'),
            onClick: () => {
              track({
                event: 'Link Clicked',
                payload: {
                  cta: 'renew_membership',
                  location: 'card',
                },
              });
            },
          }}
          textContents={[
            t('inactive_prepaid_pending_membership_renew_subtitle', {
              month: membershipOfferingPrepaidMonths(membershipOffering),
            }),
          ]}
        />
        {children}
      </>
    );
  }

  // SPOTLIGHT ITEM: Cancelled Pending
  if (cancelledSpotlightItemExperiment && pendingCancellation) {
    const currentPlanEndDate = plans?.find((plan) => plan.status === PlanStatus.PRESENT)?.endDate;
    if (!currentPlanEndDate) return <>{children}</>;
    return (
      <>
        <SpotlightItem
          data-testid="spotlight-pc"
          label={t('pending_cancellation_spotlight_item_title')}
          ctaButton={{
            to: routes.selectPlanForPendingRenew,
            children: t('membership_hub_renew_membership'),
          }}
          textContents={[
            t('pending_cancellation_spotlight_item_subtitle', {
              subscriptionEndDate: <FormattedDate value={currentPlanEndDate} format="default" />,
            }),
          ]}
        />
        {children}
      </>
    );
  }

  return <>{children}</>;
};
